import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertCircle, 
  ChevronDown, 
  ChevronRight,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Plus,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import {
  createAccount,
  previewCSV,
  analyzeCSV,
  generateForecast,
  getAccounts,
  deleteAccount,
} from '@/lib/api';
import * as analytics from '@/lib/analytics';

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking Account' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'savings', label: 'Savings Account' },
];

import { formatCurrency as fmtCurrency } from '@/lib/currency';

function formatCurrency(amount) {
  const formatted = fmtCurrency(Math.abs(amount));
  return (amount < 0 ? '-' : '+') + formatted;
}

// Simplified GroupCard for Phase 1B Rebuild - no categories
function GroupCard(props) {
  const group = props.group;
  const onToggle = props.onToggle;
  
  const isIncome = group.is_income;
  const bgClass = group.include ? 'bg-muted/30 border-border' : 'bg-muted/10 border-dashed opacity-60';
  
  function handleToggle() {
    onToggle(group.id);
  }
  
  // Format amount display
  let amountDisplay = null;
  if (Math.abs(group.min_amount - group.max_amount) < 0.01) {
    // Fixed amount
    amountDisplay = (
      <span className={isIncome ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
        {formatCurrency(group.min_amount)}
      </span>
    );
  } else {
    // Variable range
    amountDisplay = (
      <span className={isIncome ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
        {formatCurrency(group.min_amount)} - {formatCurrency(group.max_amount)}
      </span>
    );
  }
  
  // Format pattern display - use frequency if available, otherwise pattern_type
  let patternLabel = group.frequency || group.pattern_type;
  if (patternLabel === 'one_time') patternLabel = 'one-time';
  else if (patternLabel === 'recurring_fixed') patternLabel = group.frequency || 'recurring';
  else if (patternLabel === 'recurring_variable') patternLabel = group.frequency || 'recurring';
  
  return (
    <div className={'flex items-center gap-3 p-3 rounded-lg border w-full ' + bgClass}>
      <Checkbox checked={group.include} onCheckedChange={handleToggle} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{group.description}</span>
          {amountDisplay}
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <span>{group.occurrences}x</span>
          <span>•</span>
          <Badge variant="outline" className="text-xs">
            {patternLabel}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function PatternSection(props) {
  const title = props.title;
  const patterns = props.patterns;
  const onToggle = props.onToggle;
  const onToggleAll = props.onToggleAll;
  const defaultOpen = props.defaultOpen === true; // Default to collapsed
  
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  if (!patterns || patterns.length === 0) {
    return null;
  }
  
  let checkedCount = 0;
  for (let i = 0; i < patterns.length; i++) {
    if (patterns[i].include) {
      checkedCount++;
    }
  }
  
  const allChecked = checkedCount === patterns.length;
  const noneChecked = checkedCount === 0;
  
  function toggleOpen(e) {
    e.stopPropagation();
    setIsOpen(!isOpen);
  }
  
  function handleCheckAll(e) {
    e.stopPropagation();
    if (onToggleAll) {
      const patternIds = patterns.map(function(p) { return p.id; });
      onToggleAll(patternIds, true);
    }
  }
  
  function handleUncheckAll(e) {
    e.stopPropagation();
    if (onToggleAll) {
      const patternIds = patterns.map(function(p) { return p.id; });
      onToggleAll(patternIds, false);
    }
  }
  
  const items = [];
  for (let i = 0; i < patterns.length; i++) {
    const p = patterns[i];
    items.push(<PatternCard key={p.id} pattern={p} onToggle={onToggle} />);
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-2 w-full">
      <div className="flex items-center w-full p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer gap-3" onClick={toggleOpen}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
          <span className="font-medium truncate">{title}</span>
          <Badge variant="secondary" className="text-xs flex-shrink-0">{checkedCount}/{patterns.length}</Badge>
        </div>
        {isOpen && (
          <div className="flex items-center gap-1 flex-shrink-0" onClick={function(e) { e.stopPropagation(); }}>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs whitespace-nowrap"
              onClick={handleCheckAll}
              disabled={allChecked}
            >
              Check All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs whitespace-nowrap"
              onClick={handleUncheckAll}
              disabled={noneChecked}
            >
              Uncheck All
            </Button>
          </div>
        )}
      </div>
      <CollapsibleContent className="space-y-2 pl-2 max-h-[400px] overflow-y-auto overflow-x-hidden pr-2 w-full">
        {items}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function CSVImportWizard(props) {
  const open = props.open;
  const onOpenChange = props.onOpenChange;
  const onComplete = props.onComplete;
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  const [accountType, setAccountType] = useState('checking');
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState(null);
  
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState(null);
  
  const [analysisMonths, setAnalysisMonths] = useState('12');
  const [patterns, setPatterns] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [forecastMonths, setForecastMonths] = useState('24');
  
  // Existing account state
  const [existingAccount, setExistingAccount] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Check for existing account when wizard opens
  async function checkExistingAccount() {
    try {
      const response = await getAccounts();
      if (response.data && response.data.length > 0) {
        setExistingAccount(response.data[0]);
      } else {
        setExistingAccount(null);
      }
    } catch (err) {
      setExistingAccount(null);
    }
  }

  // Reset/delete existing import
  async function handleResetImport() {
    if (!existingAccount) return;
    
    setLoading(true);
    setLoadingMessage('Deleting imported data...');
    
    try {
      await deleteAccount(existingAccount.id);
      setExistingAccount(null);
      setShowResetConfirm(false);
      toast.success('CSV import reset successfully. You can now import a new CSV.');
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to reset import';
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  // Track if we've checked for existing account
  const [checkedForAccount, setCheckedForAccount] = useState(false);
  
  // Check for existing account when dialog opens
  if (open && !checkedForAccount && !loading) {
    setCheckedForAccount(true);
    checkExistingAccount();
  }
  
  // Reset check flag when dialog closes
  if (!open && checkedForAccount) {
    setCheckedForAccount(false);
  }

  function resetWizard() {
    setStep(0);
    setAccountType('checking');
    setAccountName('');
    setAccountId(null);
    setFile(null);
    setPreview(null);
    setColumnMapping(null);
    setAnalysisMonths('12');
    setPatterns(null);
    setAnalysisResult(null);
    setForecastMonths('24');
    setExistingAccount(null);
    setShowResetConfirm(false);
    setCheckedForAccount(false);
  }

  function handleClose() {
    resetWizard();
    onOpenChange(false);
  }

  async function handleCreateAccount() {
    if (!accountName.trim()) {
      toast.error('Please enter an account name');
      return;
    }
    
    setLoading(true);
    setLoadingMessage('Creating account...');
    
    try {
      const response = await createAccount({
        account_type: accountType,
        account_name: accountName.trim()
      });
      
      setAccountId(response.data.account_id);
      setStep(1);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to create account';
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  async function handleFileSelect(e) {
    const selectedFile = e.target.files && e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setFile(selectedFile);
    setLoading(true);
    setLoadingMessage('Parsing CSV...');
    
    // Track CSV import started
    analytics.trackCSVImportStarted(
      selectedFile.name,
      selectedFile.size,
      null // row count unknown at this point
    );
    
    try {
      const response = await previewCSV(selectedFile);
      
      setPreview(response.data);
      setColumnMapping(response.data.column_mapping);
      setStep(2);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to parse CSV';
      toast.error(message);
      setFile(null);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  function handleConfirmMapping() {
    setStep(3);
  }

  async function handleAnalyze() {
    if (!file || !accountId) return;
    
    setLoading(true);
    setLoadingMessage('Analyzing transactions...');
    
    try {
      const response = await analyzeCSV(file, accountId, analysisMonths, columnMapping);
      const data = response.data;
      
      setAnalysisResult(data);
      
      // Phase 1B Rebuild: Use flat groups list (no categories)
      const groups = data.groups || [];
      
      // Groups come pre-sorted by occurrence, then annual impact
      setPatterns(groups);
      setStep(4);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to analyze CSV';
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  function togglePattern(patternId) {
    const newPatterns = [];
    for (let i = 0; i < patterns.length; i++) {
      const p = patterns[i];
      if (p.id === patternId) {
        newPatterns.push({ ...p, include: !p.include });
      } else {
        newPatterns.push(p);
      }
    }
    setPatterns(newPatterns);
  }

  function toggleAllPatterns(patternIds, checked) {
    const idSet = new Set(patternIds);
    const newPatterns = [];
    for (let i = 0; i < patterns.length; i++) {
      const p = patterns[i];
      if (idSet.has(p.id)) {
        newPatterns.push({ ...p, include: checked });
      } else {
        newPatterns.push(p);
      }
    }
    setPatterns(newPatterns);
  }

  async function handleGenerateForecast() {
    if (!patterns || !accountId) return;
    
    const approvedPatterns = [];
    for (let i = 0; i < patterns.length; i++) {
      if (patterns[i].include) {
        approvedPatterns.push(patterns[i]);
      }
    }
    
    if (approvedPatterns.length === 0) {
      toast.error('Please select at least one pattern to include');
      return;
    }
    
    setLoading(true);
    setLoadingMessage('Generating forecast...');
    
    try {
      const response = await generateForecast(accountId, approvedPatterns, parseInt(forecastMonths));
      
      // Track CSV import completed
      const totalRows = preview?.row_count || 0;
      analytics.trackCSVImportCompleted(totalRows, response.data.transactions_created);
      analytics.trackForecastGenerated('csv', response.data.transactions_created, false);
      
      setStep(5);
      toast.success('Created ' + response.data.transactions_created + ' transactions!');
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to generate forecast';
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  function handleFinish() {
    handleClose();
    if (onComplete) onComplete();
  }

  // Phase 1B Rebuild: No more pattern grouping - flat list of groups
  // Groups are already sorted by occurrence, then annual impact

  // Build preview table rows
  let previewRows = null;
  if (preview && preview.preview) {
    previewRows = [];
    for (let i = 0; i < preview.preview.length; i++) {
      const row = preview.preview[i];
      const amtClass = row.amount > 0 ? 'text-green-600' : 'text-red-600';
      previewRows.push(
        <TableRow key={i}>
          <TableCell className="font-mono text-sm">{row.date}</TableCell>
          <TableCell>{row.description}</TableCell>
          <TableCell className={'text-right font-mono ' + amtClass}>
            {row.amount !== null ? formatCurrency(row.amount) : '-'}
          </TableCell>
        </TableRow>
      );
    }
  }

  // Build header select options
  let headerOptions = null;
  if (preview && preview.headers) {
    headerOptions = [];
    for (let i = 0; i < preview.headers.length; i++) {
      headerOptions.push(<SelectItem key={i} value={i.toString()}>{preview.headers[i]}</SelectItem>);
    }
  }

  const stepTitles = ['Account Setup', 'Upload CSV', 'Preview & Mapping', 'Date Range', 'Review Patterns', 'Generate Forecast'];
  const currentTitle = stepTitles[step] || 'Import';

  // Step indicators
  const stepIndicators = [];
  for (let i = 0; i < 6; i++) {
    let bgClass = 'bg-muted';
    if (i < step) bgClass = 'bg-primary';
    else if (i === step) bgClass = 'bg-primary/60';
    stepIndicators.push(<div key={i} className={'flex-1 h-1.5 rounded-full transition-colors ' + bgClass} />);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] h-[85vh] flex flex-col" data-testid="csv-import-wizard">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Import Transactions
          </DialogTitle>
          <DialogDescription>
            {currentTitle} - Step {step + 1} of 6
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 px-1 shrink-0">
          {stepIndicators}
        </div>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          {step === 0 && (
            <div className="space-y-6 py-4">
              {/* Show existing account with reset option */}
              {existingAccount && !showResetConfirm && (
                <div className="p-4 rounded-lg border border-amber-500/50 bg-amber-500/10">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-700 dark:text-amber-400">
                        You already have an imported account
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        <strong>{existingAccount.account_name}</strong> ({existingAccount.account_type.replace('_', ' ')})
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Phase 1 only supports one account. To import a new CSV, you need to reset the existing import first.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                        onClick={function() { setShowResetConfirm(true); }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Reset CSV Import
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Reset confirmation */}
              {existingAccount && showResetConfirm && (
                <div className="p-4 rounded-lg border border-red-500/50 bg-red-500/10">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-red-700 dark:text-red-400">
                        Are you sure you want to reset the CSV import?
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        This will permanently delete:
                      </p>
                      <ul className="text-sm text-muted-foreground mt-1 ml-4 list-disc">
                        <li>The imported account ({existingAccount.account_name})</li>
                        <li>All transactions from CSV import</li>
                        <li>All detected patterns</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        <strong>Manually-entered transactions will be kept.</strong>
                      </p>
                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleResetImport}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Yes, Reset Import
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={function() { setShowResetConfirm(false); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Account setup form - only show if no existing account */}
              {!existingAccount && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>What type of account is this?</Label>
                    <RadioGroup value={accountType} onValueChange={setAccountType}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="checking" id="checking" />
                        <Label htmlFor="checking" className="font-normal cursor-pointer">Checking Account</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="credit_card" id="credit_card" />
                        <Label htmlFor="credit_card" className="font-normal cursor-pointer">Credit Card</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="savings" id="savings" />
                        <Label htmlFor="savings" className="font-normal cursor-pointer">Savings Account</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="account-name">What would you like to name this account?</Label>
                    <Input
                      id="account-name"
                      placeholder="e.g., Main Checking, Chase Sapphire"
                      value={accountName}
                      onChange={function(e) { setAccountName(e.target.value); }}
                      data-testid="account-name-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6 py-4">
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={function() { document.getElementById('csv-file-input').click(); }}
              >
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Drop your CSV file here</p>
                <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="csv-file-input"
                />
              </div>
              
              {file && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-sm text-muted-foreground">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 2 && preview && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-2">
                {preview.confidence === 'high' && (
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Columns detected
                  </Badge>
                )}
                {preview.confidence === 'medium' && (
                  <Badge variant="secondary">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Please verify mapping
                  </Badge>
                )}
                {preview.confidence === 'low' && (
                  <Badge variant="destructive">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Manual mapping required
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground">
                  {preview.total_rows} transactions found
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date Column</Label>
                  <Select 
                    value={columnMapping.date_column !== null ? columnMapping.date_column.toString() : ''} 
                    onValueChange={function(v) { setColumnMapping({...columnMapping, date_column: parseInt(v)}); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>{headerOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description Column</Label>
                  <Select 
                    value={columnMapping.description_column !== null ? columnMapping.description_column.toString() : ''} 
                    onValueChange={function(v) { setColumnMapping({...columnMapping, description_column: parseInt(v)}); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>{headerOptions}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount Column</Label>
                  <Select 
                    value={columnMapping.amount_column !== null ? columnMapping.amount_column.toString() : 'none'} 
                    onValueChange={function(v) { setColumnMapping({...columnMapping, amount_column: v === 'none' ? null : parseInt(v)}); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not applicable</SelectItem>
                      {headerOptions}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Debit Column (optional)</Label>
                  <Select 
                    value={columnMapping.debit_column !== null ? columnMapping.debit_column.toString() : 'none'} 
                    onValueChange={function(v) { setColumnMapping({...columnMapping, debit_column: v === 'none' ? null : parseInt(v)}); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not applicable</SelectItem>
                      {headerOptions}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Preview (first 10 rows)</Label>
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{previewRows}</TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {step === 3 && preview && (
            <div className="space-y-6 py-4">
              {preview.date_range && (
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm">
                    Found <strong>{preview.date_range.months_available} months</strong> of data
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {preview.date_range.earliest} to {preview.date_range.latest}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Label>How much historical data should we analyze for patterns?</Label>
                <RadioGroup value={analysisMonths} onValueChange={setAnalysisMonths}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="6" id="months-6" />
                    <Label htmlFor="months-6" className="font-normal cursor-pointer">
                      6 months (faster, recent patterns only)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="12" id="months-12" />
                    <Label htmlFor="months-12" className="font-normal cursor-pointer">
                      12 months (recommended - captures full year seasonality)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="24" id="months-24" />
                    <Label htmlFor="months-24" className="font-normal cursor-pointer">
                      All available data
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-muted-foreground">
                  More data = better pattern detection, but may include outdated habits.
                </p>
              </div>
            </div>
          )}

          {step === 4 && patterns && analysisResult && (
            <div className="space-y-4 py-4">
              {/* Summary Banner */}
              <div className="grid grid-cols-4 gap-4 p-4 rounded-lg bg-muted/30 border">
                <div className="text-center">
                  <p className="text-2xl font-bold">{patterns.length}</p>
                  <p className="text-xs text-muted-foreground">Groups</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{analysisResult.transactions_analyzed}</p>
                  <p className="text-xs text-muted-foreground">Transactions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(analysisResult.summary.total_income)}
                  </p>
                  <p className="text-xs text-muted-foreground">Income</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(analysisResult.summary.total_expenses)}
                  </p>
                  <p className="text-xs text-muted-foreground">Expenses</p>
                </div>
              </div>

              {/* Check All / Uncheck All Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allIds = patterns.map(p => p.id);
                    toggleAllPatterns(allIds, true);
                  }}
                >
                  Check All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allIds = patterns.map(p => p.id);
                    toggleAllPatterns(allIds, false);
                  }}
                >
                  Uncheck All
                </Button>
              </div>

              {/* Flat Groups List - Single scrollable area */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {patterns.map(group => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    onToggle={togglePattern}
                  />
                ))}
              </div>

              <Separator />

              <div className="space-y-4">
                <Label>Forecast Settings</Label>
                <div className="space-y-2">
                  <Label className="text-xs">Generate forecast for</Label>
                  <Select value={forecastMonths} onValueChange={setForecastMonths}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                      <SelectItem value="36">36 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="py-8 text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Import Complete!</h3>
                <p className="text-muted-foreground mt-2">
                  Your forecast has been generated successfully.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold">{analysisResult ? analysisResult.transactions_analyzed : 0}</p>
                  <p className="text-xs text-muted-foreground">Imported</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold">{patterns ? patterns.filter(function(p) { return p.include; }).length : 0}</p>
                  <p className="text-xs text-muted-foreground">Patterns</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold">{forecastMonths}</p>
                  <p className="text-xs text-muted-foreground">Months</p>
                </div>
              </div>
            </div>
          )}
        </ScrollArea>

        {loading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50 rounded-lg">
            <div className="text-center space-y-3">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground">{loadingMessage}</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={function() { if (step > 0 && step < 5) setStep(step - 1); else handleClose(); }}
            disabled={loading}
          >
            {step === 0 || step === 5 ? 'Cancel' : (
              <span className="flex items-center"><ArrowLeft className="w-4 h-4 mr-2" />Back</span>
            )}
          </Button>

          {step === 0 && !existingAccount && (
            <Button onClick={handleCreateAccount} disabled={loading || !accountName.trim()}>
              Continue<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 0 && existingAccount && (
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          )}

          {step === 1 && (
            <Button disabled={true}>
              Upload CSV to continue<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 2 && (
            <Button onClick={handleConfirmMapping} disabled={loading}>
              Looks correct<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 3 && (
            <Button onClick={handleAnalyze} disabled={loading}>
              Analyze Patterns<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 4 && (
            <Button onClick={handleGenerateForecast} disabled={loading}>
              Generate Forecast<CheckCircle2 className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 5 && (
            <Button onClick={handleFinish}>
              View Forecast<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
